import { Injectable } from '@angular/core';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { DropdownListSettingsLanguageModel } from '@fi-shared/dropdown';
import { ContentLanguageLabelModel } from '@fi-shared/models';
import { getTextIdByApplicationLanguageType } from '../../shared/fi-content-language-shared/get-tex-id-by-application-language-type';
import { AppTextService } from '@fi-shared/app-text';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { FiDialogSelectLanguageComponent } from '@fi-app/components/dialog-select-language/dialog-select-language.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import {
    IContentLanguage,
    IContentLanguageLabelModel,
    IDropdownSelectLanguageSettingsItem,
} from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { createMap } from '@fi-shared/utils/create-map';

@Injectable()
export class ContentLanguagesService {
    constructor(
        private sessionService: SessionService,
        private appTextService: AppTextService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private fiDialogsService: FiDialogsService,
    ) {}

    // todo use FiContentLanguageSharedService
    GetContentLanguages(): IContentLanguage[] {
        return this.customerDataService.customerData.ContentLanguages;
    }

    GetDropdownListSettingsLanguage(): DropdownListSettingsLanguageModel<IDropdownSelectLanguageSettingsItem> {
        const settings = new DropdownListSettingsLanguageModel<IDropdownSelectLanguageSettingsItem>();
        settings.setDataSet(
            this.GetContentLanguages().map((lang: IContentLanguage) => {
                let Name = lang.Name;
                if (lang.IsDefaultLanguage) {
                    settings.defaultLanguageId = lang.Id;
                }
                return {
                    Name: Name,
                    Id: lang.Id,
                    IsDefaultLanguage: lang.IsDefaultLanguage,
                };
            }),
        );

        return settings;
    }

    // todo use FiContentLanguageSharedService
    GetApplicationLanguages() {
        const applicationLanguages: IContentLanguage[] =
            this.customerDataService.customerData.ApplicationLanguageAccess.map((applicationLanguageType) => ({
                Id: applicationLanguageType,
                Name: this.appTextService.get(getTextIdByApplicationLanguageType(applicationLanguageType)),
                IsDefaultLanguage:
                    applicationLanguageType === this.customerDataService.customerData.DefaultApplicationLanguageType,
                SortIndex: undefined,
            }));

        return applicationLanguages;
    }

    GetMonthShortNames() {
        var result = [
            { Id: 0, Name: this.appTextService.get('month.jan') },
            { Id: 1, Name: this.appTextService.get('month.feb') },
            { Id: 2, Name: this.appTextService.get('month.mar') },
            { Id: 3, Name: this.appTextService.get('month.apr') },
            { Id: 4, Name: this.appTextService.get('month.may') },
            { Id: 5, Name: this.appTextService.get('month.june') },
            { Id: 6, Name: this.appTextService.get('month.july') },
            { Id: 7, Name: this.appTextService.get('month.aug') },
            { Id: 8, Name: this.appTextService.get('month.sept') },
            { Id: 9, Name: this.appTextService.get('month.oct') },
            { Id: 10, Name: this.appTextService.get('month.nov') },
            { Id: 11, Name: this.appTextService.get('month.dec') },
        ];

        return result;
    }

    UpdateContentLanguageNames(contentLanguages?: IContentLanguage[]) {
        contentLanguages ||
            this.GetContentLanguages().forEach((item) => {
                item.Name = this.appTextService.get(getTextIdByApplicationLanguageType(item.Id));
            });
    }

    GetContentLanguageById(id: number) {
        return this.GetContentLanguages().find((item) => item.Id === id);
    }

    GetDefaultLanguage() {
        return this.GetContentLanguages().find((item) => item.IsDefaultLanguage);
    }

    GetDefaultLanguageId() {
        var language = this.GetDefaultLanguage();
        return language ? language.Id : null;
    }

    GetDefaultLanguageIdx() {
        const idx = this.GetContentLanguages().findIndex((item) => item.IsDefaultLanguage);
        return idx !== -1 ? idx : null;
    }

    InitializeContentLanguages(item, response?) {
        if (!response) {
            response = {};
        }
        this.InitializeContentLanguagesArray(item.Names, response.Names || []);
        if (item.Descriptions) {
            this.InitializeContentLanguagesArray(item.Descriptions, response.Descriptions || []);
        }
    }

    GetDefaultOrSelectedContentLanguageLabel(names: IContentLanguageLabelModel[], selectedLanguageId: number) {
        const defaultLanguageId = this.GetDefaultLanguageId();
        const sortedNames = names
            .filter((item) => item.Value)
            .sort(
                comparator((item: IContentLanguageLabelModel) => {
                    if (item.ContentLanguageId === defaultLanguageId) return 0;
                    if (item.ContentLanguageId === selectedLanguageId) return 1;
                    return 2;
                }),
            );
        return sortedNames.length ? sortedNames[0].Value : '';
    }

    // todo use FiContentLanguageSharedService
    /**
     * roles, ares, business areas and сlusters use system languages for its names and descriptions
     */
    InitializeContentLanguagesArray(
        arr: ContentLanguageLabelModel[],
        data: {
            ContentLanguageId: number;
            Value: string;
            SortIndex?: number;
        }[],
        useApplicationLanguages = false,
    ) {
        if (!data) {
            data = [];
        }
        const contentLanguages = !useApplicationLanguages
            ? this.customerDataService.customerData.ContentLanguages
            : this.GetApplicationLanguages();
        const dataMap = createMap(data, 'ContentLanguageId');
        arr.length = 0; // https://chainformation.atlassian.net/browse/FI-2951
        for (var i = 0; i < contentLanguages.length; i++) {
            arr.push(
                new ContentLanguageLabelModel(
                    contentLanguages[i].Id,
                    dataMap.hasOwnProperty(contentLanguages[i].Id) ? dataMap[contentLanguages[i].Id].Value : '',
                    contentLanguages[i].SortIndex,
                ),
            );
        }
    }

    getSelectedLanguageIdByApplicationLang(names: IContentLanguageLabelModel[]): number {
        const preferredName = names.find((name) => name.ContentLanguageId === this.userDataService.applicationLanguage);

        if (!preferredName?.Value && names.some((name) => name.Value)) {
            const existingNames = names.filter((name) => name.Value);
            if (existingNames.length)
                return existingNames.sort(comparator((name) => name.SortIndex))[0].ContentLanguageId;
        }
        return this.userDataService.applicationLanguage;
    }

    openSelectLanguageDialog(
        languageId: number,
        saveLanguageOnChange: boolean = false,
        itemLanguageIds?: number[],
    ): IFiDialogResult<number> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiDialogSelectLanguageComponent,
            contentComponentData: {
                languageId,
                saveLanguageOnChange,
                itemLanguageIds,
            },
            config: {
                panelClass: 'fi-dialog-select-language-panel',
            },
        });
    }
}
